import { useContext, useEffect, useReducer, useState, useRef } from 'react'
import clsx from 'clsx'

import List from '@material-ui/core/List'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'

import TicketListItemCustom from '../TicketListItemCustom'
import TicketsListSkeleton from '../TicketsListSkeleton'

import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import useTickets from '../../hooks/useTickets'
import { i18n } from '../../translate/i18n'
import toastError from '../../errors/toastError'

import { Frown } from 'lucide-react'
import { useStyles } from './style'

const reducer = (state, action) => {
  if (action.type === 'LOAD_TICKETS') {
    const newTickets = action.payload

    newTickets.forEach(ticket => {
      const ticketIndex = state.findIndex(t => t.id === ticket.id)
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0])
        }
      } else {
        state.push(ticket)
      }
    })

    return [...state]
  }

  if (action.type === 'RESET_UNREAD') {
    const ticketId = action.payload

    const ticketIndex = state.findIndex(t => t.id === ticketId)
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0
    }

    return [...state]
  }

  if (action.type === 'UPDATE_TICKET') {
    const ticket = action.payload

    const ticketIndex = state.findIndex(t => t.id === ticket.id)
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1)
    }

    const idx = state.findIndex(
      t =>
        new Date(t.updatedAt) < new Date(ticket.updatedAt) ||
        (t.updatedAt === ticket.updatedAt && t.id < ticket.id)
    )
    state.splice(idx < 0 ? state.length : idx, 0, ticket)

    return [...state]
  }

  if (action.type === 'UPDATE_TICKET_UNREAD_MESSAGES') {
    const ticket = action.payload

    const ticketIndex = state.findIndex(t => t.id === ticket.id)
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket
      state.unshift(state.splice(ticketIndex, 1)[0])
    } else {
      state.unshift(ticket)
    }

    return [...state]
  }

  if (action.type === 'UPDATE_TICKET_CONTACT') {
    const contact = action.payload
    const ticketIndex = state.findIndex(t => t.contactId === contact.id)
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact
    }
    return [...state]
  }

  if (action.type === 'UPDATE_TICKET_PRESENCE') {
    const data = action.payload
    const ticketIndex = state.findIndex(t => t.id === data.ticketId)
    if (ticketIndex !== -1) {
      state[ticketIndex].presence = data.presence
    }
    return [...state]
  }

  if (action.type === 'DELETE_TICKET') {
    const ticketId = action.payload
    const ticketIndex = state.findIndex(t => t.id === ticketId)
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1)
    }

    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }

  if (action.type === 'SET_TICKETS') {
    return action.payload
  }
}

const TicketsListCustom = props => {
  const {
    status,
    groups,
    chatbots,
    isSearch,
    searchParam,
    tags,
    users,
    contactId,
    contactIds,
    noQueue,
    dateFrom,
    dateTo,
    closingDateFrom,
    closingDateTo,
    showAll,
    selectedQueueIds,
    whatsappIds,
    sortBy,
    sortOrder,
    itemSize,
    columns = 1,
    showReportDetails,
    updateCount,
    updateTotal,
    style,
    setTabOpen,
    showTabGroups,
    filteredTickets,
    updateUnreadStatus,
    limit,
    disableInfiniteScroll,
    controlledPageNumber,
    onPageNumberChange,
    onLoadingChange
  } = props

  const classes = useStyles()
  const [internalPageNumber, setInternalPageNumber] = useState(1)
  const pageNumber = controlledPageNumber ?? internalPageNumber
  const setPageNumber = onPageNumberChange ?? setInternalPageNumber
  const [sortConfig, setSortConfig] = useState({ column: sortBy, order: sortOrder })
  // const [update, setUpdate] = useState(0)
  const [ticketsList, dispatch] = useReducer(reducer, [])
  const ticketsListRef = useRef(ticketsList)
  useEffect(() => {
    ticketsListRef.current = ticketsList
  }, [ticketsList])
  // const [ticketsListUpdated, setTicketsListUpdated] = useState([])
  const { user } = useContext(AuthContext)
  const { profile, queues } = user
  const socketManager = useContext(SocketContext)

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [
    status,
    chatbots,
    searchParam,
    showAll,
    tags,
    groups,
    users,
    contactId,
    noQueue,
    dateFrom,
    dateTo,
    closingDateFrom,
    closingDateTo,
    selectedQueueIds,
    whatsappIds,
    limit,
    sortConfig,
    filteredTickets // Adicionado
  ])

  const {
    tickets,
    count,
    hasMore,
    loading,
    refetch: refetchTickets,
    fetchSince
  } = useTickets({
    pageNumber,
    limit,
    all: disableInfiniteScroll,
    isSearch,
    searchParam,
    status,
    groups,
    chatbots,
    showAll,
    contactId,
    noQueue,
    dateFrom,
    dateTo,
    closingDateFrom,
    closingDateTo,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    contactIds: JSON.stringify(contactIds),
    queueIds: JSON.stringify(selectedQueueIds),
    whatsappIds: JSON.stringify(whatsappIds),
    sortBy: sortConfig.column,
    sortOrder: sortConfig.order
  })

  // useEffect(() => {
  //   if (filteredTickets?.length > 0) {
  //     dispatch({ type: 'LOAD_TICKETS', payload: filteredTickets })
  //   } else {
  //     const queueIds = queues.map(q => q.id)
  //     const normalTickets = tickets
  //       .filter(ticket => !ticket.waitrating)
  //       .filter(ticket => {
  //         if (profile === 'user') {
  //           return queueIds.indexOf(ticket.queueId) > -1
  //         }
  //         return true
  //       })

  //     dispatch({ type: 'LOAD_TICKETS', payload: normalTickets })
  //   }
  // }, [tickets, queues, profile, filteredTickets, status])

  const prevUnreadRef = useRef(null)

  useEffect(() => {
    if (typeof updateUnreadStatus === 'function' && status) {
      const hasUnread = ticketsList.some(ticket => ticket.unreadMessages > 0)

      // Só despacha para o componente pai se o estado de não lidas realmente mudou,
      // evitando re-renders desnecessários e chamadas repetidas na árvore.
      if (prevUnreadRef.current !== hasUnread) {
        prevUnreadRef.current = hasUnread
        updateUnreadStatus(status, hasUnread)
      }
    }
  }, [ticketsList, status, updateUnreadStatus])

  useEffect(() => {
    updateTotal && updateTotal(count)
  }, [count, updateTotal])

  useEffect(() => {
    onLoadingChange && onLoadingChange(loading)
  }, [loading, onLoadingChange])

  useEffect(() => {
    if (disableInfiniteScroll) {
      // Modo paginado (ex.: relatórios): confia integralmente no resultado já filtrado/paginado pelo backend.
      const pageStart = (pageNumber - 1) * limit
      dispatch({
        type: 'SET_TICKETS',
        payload: tickets.slice(pageStart, pageStart + limit)
      })
      return
    }

    if (filteredTickets?.length > 0) {
      dispatch({ type: 'LOAD_TICKETS', payload: filteredTickets })
    } else {
      const queueIds = queues.map(q => q.id)

      const filterTicketsForTab = ticket => {
        if (profile === 'user') {
          if (queueIds.indexOf(ticket.queueId) === -1) {
            return false
          }
        }

        if (chatbots) {
          return !!ticket.chatbot && ticket.status === 'pending'
        }

        if (status === 'pending') {
          return (
            ticket.status === 'pending' &&
            (ticket.chatbot === false || ticket.chatbot === null) &&
            ticket.isGroup === false
          )
        }

        if (status === 'open' || status === 'closed') {
          const matchesGroupLogic = showTabGroups
            ? groups
              ? ticket.isGroup === true
              : ticket.isGroup === false
            : true

          return (
            ticket.status === status &&
            (ticket.chatbot === false || ticket.chatbot === null) &&
            matchesGroupLogic
          )
        }
        if (groups) {
          return ticket.isGroup === true
        }
        return true
      }

      const finalTickets = tickets
        .filter(ticket => !ticket.waitrating)
        .filter(filterTicketsForTab)

      dispatch({ type: 'LOAD_TICKETS', payload: finalTickets })
    }
  }, [
    tickets,
    queues,
    profile,
    filteredTickets,
    status,
    chatbots,
    groups,
    showTabGroups,
    disableInfiniteScroll,
    pageNumber,
    limit
  ])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const shouldUpdateTicket = ticket =>
      (!ticket.userId || ticket.userId === user?.id || showAll) &&
      (!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1)

    const notBelongsToUserQueues = ticket =>
      ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1

    const onConnectTicketList = () => {
      if (status) {
        socket.emit('joinTickets', status)
      }
    }

    const onCompanyTicket = data => {
      if (data.action === 'updateUnread') {
        dispatch({
          type: 'RESET_UNREAD',
          payload: data.ticketId
        })
        return
      }

      if (data.action === 'update' && status && data.ticket.status !== status) {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticket?.id })
        return
      }

      if (
        data.action === 'update' &&
        !groups &&
        data.ticket.chatbot !== !!chatbots
      ) {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticket?.id })
        return
      }

      if (
        data.action === 'update' &&
        data.ticket.status === status &&
        data.ticket.chatbot === !!chatbots &&
        shouldUpdateTicket(data.ticket)
      ) {
        dispatch({
          type: 'UPDATE_TICKET',
          payload: data.ticket
        })
      }

      if (
        groups &&
        data.action === 'update' &&
        data.ticket.isGroup &&
        shouldUpdateTicket(data.ticket)
      ) {
        dispatch({
          type: 'UPDATE_TICKET',
          payload: data.ticket
        })
      }

      if (data.action === 'update' && notBelongsToUserQueues(data.ticket)) {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticket?.id })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_TICKET', payload: data?.ticketId })
      }

      if (data.action === 'removeFromList') {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticketId })
      }
    }

    const onCompanyAppMessage = data => {
      console.debug('appMessage event received', data)
      if (showTabGroups && !!data.ticket?.isGroup !== !!groups) {
        return
      }

      const queueIds = queues.map(q => q.id)
      if (
        profile === 'user' &&
        (queueIds.indexOf(data.ticket?.queue?.id) === -1 ||
          data.ticket.queue === null)
      ) {
        return
      }

      if (
        data.action === 'create' &&
        (!showTabGroups || !!data.ticket?.isGroup === !!groups) &&
        !!data.ticket?.chatbot === !!chatbots &&
        shouldUpdateTicket(data.ticket) &&
        (status === undefined || data.ticket.status === status)
      ) {
        dispatch({
          type: 'UPDATE_TICKET_UNREAD_MESSAGES',
          payload: data.ticket
        })
      }
    }

    const onCompanyContact = data => {
      if (data.action === 'update') {
        dispatch({
          type: 'UPDATE_TICKET_CONTACT',
          payload: data.contact
        })
      }
    }

    socketManager.onConnect(onConnectTicketList)

    socket.on(`company-${companyId}-ticket`, onCompanyTicket)
    socket.on(`company-${companyId}-appMessage`, onCompanyAppMessage)
    socket.on(`company-${companyId}-contact`, onCompanyContact)
    socket.on('wsRefreshRequired', refreshRequired => {
      if (refreshRequired) {
        const currentList = ticketsListRef.current
        if (currentList.length > 0) {
          const maxUpdatedAt = currentList.reduce(
            (max, ticket) => (ticket.updatedAt > max ? ticket.updatedAt : max),
            currentList[0].updatedAt
          )

          fetchSince(maxUpdatedAt)
            .then(newTickets => {
              newTickets.forEach(ticket => {
                dispatch({ type: 'UPDATE_TICKET', payload: ticket })
              })
            })
            .catch(err => {
              toastError(err)
            })
        } else {
          dispatch({ type: 'RESET' })
          setPageNumber(1)
          refetchTickets()
        }
      }
    })

    socket.on(`company-${companyId}-presence`, data => {
      dispatch({
        type: 'UPDATE_TICKET_PRESENCE',
        payload: data
      })
    })

    return () => {
      socket.disconnect()
    }
  }, [
    status,
    showAll,
    groups,
    showTabGroups,
    chatbots,
    user,
    selectedQueueIds,
    tags,
    users,
    profile,
    queues,
    socketManager,
    refetchTickets,
    fetchSince
  ])

  useEffect(() => {
    if (typeof updateCount === 'function') {
      updateCount(ticketsList.length)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList])

  const loadMore = () => {
    setPageNumber(pageNumber + (loading ? 0 : 1))
  }

  const handleScroll = e => {
    if (disableInfiniteScroll || !hasMore || loading) return

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const handleSortClick = column => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return { column, order: prev.order === 'asc' ? 'desc' : 'asc' }
      }
      return { column, order: 'asc' }
    })
  }

  const getSortIndicator = column => {
    if (sortConfig.column !== column) return null
    return sortConfig.order === 'asc' ? ' ▲' : ' ▼'
  }

  const reportColumns = [
    { key: 'id', label: 'Protocolo' },
    { key: 'contact', label: 'Contato' },
    { key: 'connection', label: 'Conexão' },
    { key: 'queue', label: 'Departamento' },
    { key: 'status', label: 'Status' },
    { key: 'user', label: 'Último usuário' },
    { key: 'tags', label: 'Tags' },
    { key: 'createdAt', label: 'Abertura' },
    { key: 'finishedAt', label: 'Fechamento' }
  ]

  const reportTableHeader = (
    <TableHead className={classes.reportTableHead}>
      <TableRow>
        {reportColumns.map(col => (
          <TableCell
            key={col.key}
            className={classes.reportHeaderCell}
            onClick={() => handleSortClick(col.key)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {col.label}
            {getSortIndicator(col.key)}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )

  return (
    <Paper elevation={0} className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        {showReportDetails ? (
          <TableContainer className={classes.reportTableContainer}>
            <Table size="small" stickyHeader>
              {reportTableHeader}
              <TableBody>
                {ticketsList.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className={classes.reportEmptyCell}>
                      <div className={classes.noTicketsDiv}>
                        <span className={classes.noTicketsTitle}>
                          <Frown />
                          {i18n.t('ticketsList.noTicketsTitle')}
                        </span>
                        <p className={classes.noTicketsText}>
                          {i18n.t('ticketsList.noTicketsMessage')}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {ticketsList.map(ticket => (
                      <TicketListItemCustom
                        ticket={ticket}
                        itemSize={itemSize}
                        showReportDetails={showReportDetails}
                        setTabOpen={setTabOpen}
                        key={ticket.id}
                        groupActionButtons={!groups && !showTabGroups}
                      />
                    ))}
                  </>
                )}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <TicketsListSkeleton />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <List
            className={clsx(
              classes.ticketsBoxList,
              classes[`ticketsBoxList${columns}Columns`]
            )}
          >
            {ticketsList.length === 0 && !loading ? (
              <div className={classes.noTicketsDiv}>
                <span className={classes.noTicketsTitle}>
                  <Frown />
                  {i18n.t('ticketsList.noTicketsTitle')}
                </span>
                <p className={classes.noTicketsText}>
                  {i18n.t('ticketsList.noTicketsMessage')}
                </p>
              </div>
            ) : (
              <>
                {ticketsList.map(ticket => (
                  <TicketListItemCustom
                    ticket={ticket}
                    itemSize={itemSize}
                    showReportDetails={showReportDetails}
                    setTabOpen={setTabOpen}
                    key={ticket.id}
                    groupActionButtons={!groups && !showTabGroups}
                  />
                ))}
              </>
            )}
            {loading && <TicketsListSkeleton />}
          </List>
        )}
      </Paper>
    </Paper>
  )
}

export default TicketsListCustom
